import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

import { formatCurrency } from "../../utils/currency";

import SelectAcreedorModal from "../SelectAcreedorModal";
import ApoderadosApi from "api/apoderadosApi";

function EditDeudaModal({ open, onClose, onSubmit, deuda, clasesDeuda }) {
  const [formData, setFormData] = useState({
    acreedor: deuda.str_acreedor,
    clase_deuda: deuda.clase_deuda,
    capital: deuda.capital,
    capitalFormatted: formatCurrency(deuda.capital).formatted,
    reconoce_intereses: deuda.reconoce_intereses,
    suma_intereses: deuda.suma_intereses,
    intereses: deuda.intereses,
    interesesFormatted: deuda.intereses ? formatCurrency(deuda.intereses).formatted : "",
    naturaleza: deuda.naturaleza,
    //dias_mora: deuda.dias_mora,
    rango_mora: deuda.rango_mora,
    tipo_garantia: deuda.tipo_garantia,
    fecha_otorgamiento: deuda.fecha_otorgamiento,
    fecha_vencimiento: deuda.fecha_vencimiento,
    garantia: deuda.garantia,
    codeudor_avalista: deuda.codeudor_avalista,
    apoderado: deuda.apoderado || null,
    postergado: deuda.postergado,
    costas: deuda.costas,
    // Campos para pagador_libranza
    pagador_libranza: deuda.pagador_libranza || {
      nombre_completo: "",
      email: "",
      tipo_identificacion: "",
      identificacion: "",
      direccion: "",
      celular: "",
    },
  });
  const [apoderados, setApoderados] = useState([]);
  const [showAcreedorModal, setShowAcreedorModal] = useState(false);

  const fetchDatosIniciales = async () => {
    try {
      const [apoderadosRes] = await Promise.all([
        ApoderadosApi.fetchApoderadosAcreedor(formData.acreedor.id),
      ]);
      if (apoderadosRes.data && !apoderadosRes.data.message) {
        setApoderados(apoderadosRes.data || []);
      }
    } catch (error) {
      console.error("Error al obtener los datos iniciales:", error);
    }
  };

  useEffect(() => {
    if (deuda?.acreedor?.id) {
      fetchDatosIniciales();
    }
  }, [deuda?.acreedor?.id]);

  const obtenerApoderados = async (acreedorId) => {
    if (!acreedorId) return; // Evita llamadas innecesarias
    try {
      const response = await ApoderadosApi.fetchApoderadosAcreedor(acreedorId);
      if (response.data && !response.data.message) {
        setApoderados(response.data || []);
      } else {
        setApoderados([]);
      }
    } catch (error) {
      console.error("Error al obtener apoderados:", error);
      setApoderados([]);
    }
  };

  useEffect(() => {
    if (formData.acreedor?.id) {
      obtenerApoderados(formData.acreedor.id);
    }
  }, [formData.acreedor?.id]);

  const handleAcreedorSelect = (acreedorSeleccionado) => {
    setFormData((prev) => ({
      ...prev,
      acreedor: acreedorSeleccionado,
      apoderado: null,
    }));
    if (acreedorSeleccionado?.id) {
      obtenerApoderados(acreedorSeleccionado.id);
    }
    setShowAcreedorModal(false);
  };

  const handleDateChange = (field) => (newValue) => {
    if (newValue) {
      const year = newValue.$y;
      const month = String(newValue.$M + 1).padStart(2, "0");
      const day = String(newValue.$D).padStart(2, "0");
      const fecha = `${year}-${month}-${day}`;
      setFormData({
        ...formData,
        [field]: fecha,
      });
    } else {
      setFormData({
        ...formData,
        [field]: null,
      });
    }
  };

  const handleMoneyChange = (field) => (event) => {
    const { formatted, value } = formatCurrency(event.target.value);
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      [`${field}Formatted`]: formatted,
    }));
  };

  const handleChange = (field) => (event) => {
    let value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    if (field === "clase_deuda") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        naturaleza: formData.naturaleza,
      }));
    } else if (field === "naturaleza") {
      value = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    } else {
      //alert("field: " + field + ", value: " + value);
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Nuevo manejador para campos del pagador de libranza
  const handlePagadorLibranzaChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      pagador_libranza: {
        ...prev.pagador_libranza,
        [field]: value,
      },
    }));
  };

  // Verificar si la naturaleza contiene "Libranza" (case insensitive)
  const isLibranza = formData.naturaleza && formData.naturaleza.toLowerCase().includes("libranza");

  const handleSubmit = () => {
    const claseDeuda = clasesDeuda.find((c) => c.id === Number(formData.clase_deuda));
    if (!claseDeuda) return;

    // Preparar pagador_libranza solo si es libranza y tiene datos
    let pagadorLibranzaData = null;
    if (isLibranza) {
      const { pagador_libranza } = formData;
      // Solo incluir pagador_libranza si al menos tiene nombre_completo
      if (pagador_libranza.nombre_completo.trim()) {
        pagadorLibranzaData = {
          nombre_completo: pagador_libranza.nombre_completo.trim(),
          email: pagador_libranza.email.trim(),
          tipo_identificacion: pagador_libranza.tipo_identificacion.trim(),
          identificacion: pagador_libranza.identificacion.trim(),
          direccion: pagador_libranza.direccion.trim(),
          celular: pagador_libranza.celular.trim(),
        };
      }
    }

    const deudaActualizada = {
      ...deuda,
      acreedor: formData.acreedor.id,
      clase_deuda: formData.clase_deuda,
      capital: formData.capital,
      reconoce_intereses: formData.reconoce_intereses,
      suma_intereses: formData.suma_intereses,
      intereses: formData.intereses,
      naturaleza: formData.naturaleza,
      codeudor_avalista: formData.codeudor_avalista,
      //dias_mora: formData.dias_mora,
      rango_mora: formData.rango_mora,
      tipo_garantia: formData.tipo_garantia,
      fecha_otorgamiento: formData.fecha_otorgamiento || null,
      fecha_vencimiento: formData.fecha_vencimiento || null,
      garantia: formData.garantia,
      str_acreedor: formData.acreedor,
      str_clase_deuda: {
        id: claseDeuda.id,
        nombre: claseDeuda.nombre,
        descripcion: claseDeuda.descripcion,
      },
      apoderado: formData.apoderado,
      postergado: formData.postergado,
      costas: formData.costas,
      pagador_libranza: pagadorLibranzaData, // Nuevo campo
    };
    //alert(JSON.stringify(deudaActualizada));
    onSubmit(deudaActualizada);
  };

  const claseActual = formData.clase_deuda
    ? clasesDeuda.find((c) => c.id === Number(formData.clase_deuda))
    : null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <SuiTypography variant="h5">Editar Deuda</SuiTypography>
        </DialogTitle>
        <DialogContent>
          <SuiBox py={3}>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Acreedor
                  </SuiTypography>
                  <SuiButton
                    variant="gradient"
                    buttonColor="info"
                    fullWidth
                    onClick={() => setShowAcreedorModal(true)}
                  >
                    {formData.acreedor
                      ? `${formData.acreedor.nombre} ${formData.acreedor.apellido || ""}`
                      : "Seleccionar Acreedor"}
                  </SuiButton>
                </SuiBox>
              </Grid>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Apoderado Acreedor para esta Deuda
                  </SuiTypography>
                  <Select value={formData.apoderado} onChange={handleChange("apoderado")}>
                    <MenuItem value="">
                      <em>Seleccione un Apoderado</em>
                    </MenuItem>
                    {apoderados.map((apoderado) => (
                      <MenuItem key={apoderado.id} value={apoderado.id}>
                        {apoderado.nombre_completo}
                      </MenuItem>
                    ))}
                  </Select>
                  {/* <Autocomplete
                    disablePortal
                    options={apoderados}
                    getOptionLabel={(option) => option?.nombre_completo || ""}
                    sx={{ mb: 3 }}
                    renderInput={(params) => <TextField {...params} />}
                    value={formData.apoderado || null}
                    onChange={handleChange("apoderado")}
                  /> */}
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={12}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Clase Deuda
                  </SuiTypography>
                  <FormControl fullWidth>
                    <Select value={formData.clase_deuda} onChange={handleChange("clase_deuda")}>
                      {clasesDeuda.map((clase) => (
                        <MenuItem key={clase.id} value={clase.id}>
                          {clase.nombre} - {clase.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Tipo Garantía
                  </SuiTypography>
                  {!claseActual?.tipos_garantia && (
                    <SuiInput
                      value={formData.tipo_garantia}
                      onChange={handleChange("tipo_garantia")}
                      placeholder="Tipo Garantía"
                      fullWidth
                    />
                  )}
                  {claseActual?.tipos_garantia?.length > 0 && (
                    <Select value={formData.tipo_garantia} onChange={handleChange("tipo_garantia")}>
                      <MenuItem value="">Seleccione un Tipo Garantía</MenuItem>
                      {claseActual.tipos_garantia.map((gar) => (
                        <MenuItem key={gar} value={gar}>
                          {gar.toUpperCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </SuiBox>
              </Grid>
              {formData.tipo_garantia === "Otro" && (
                <Grid size={6}>
                  <SuiBox mb={2}>
                    <SuiTypography variant="h6" fontWeight="dark">
                      ¿Cuál?
                    </SuiTypography>
                    <SuiInput
                      value={formData.garantia}
                      onChange={handleChange("garantia")}
                      placeholder="Garantía"
                      fullWidth
                    />
                  </SuiBox>
                </Grid>
              )}
              {formData.tipo_garantia === "Libranza" && (
                <Grid size={6}>
                  <SuiBox mb={2}>
                    <SuiTypography variant="h6" fontWeight="dark">
                      Correo Pagador
                    </SuiTypography>
                    <SuiInput
                      type="email"
                      value={formData.correo_pagador}
                      onChange={handleChange("correo_pagador")}
                      placeholder="Correo del Pagador"
                      fullWidth
                    />
                  </SuiBox>
                </Grid>
              )}
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Monto Deuda
                  </SuiTypography>
                  <SuiInput
                    value={formData.capitalFormatted}
                    onChange={handleMoneyChange("capital")}
                    placeholder="Monto de la Deuda"
                    fullWidth
                  />
                </SuiBox>
              </Grid>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Rango Mora
                  </SuiTypography>
                  <Select
                    value={formData.rango_mora}
                    onChange={handleChange("rango_mora")}
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="AL DIA">AL DIA</MenuItem>
                    <MenuItem value="MENOS DE 90 DIAS">MENOS DE 90 DIAS</MenuItem>
                    <MenuItem value="MAS DE 90 DIAS">MAS DE 90 DIAS</MenuItem>
                  </Select>
                  {/* <SuiInput
                    type="number"
                    value={formData.dias_mora}
                    onChange={handleChange("dias_mora")}
                    placeholder="Días en Mora"
                    fullWidth
                  /> */}
                </SuiBox>
              </Grid>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    Naturaleza
                  </SuiTypography>
                  <SuiInput
                    value={formData.naturaleza}
                    onChange={handleChange("naturaleza")}
                    placeholder="Naturaleza"
                    fullWidth
                  />
                </SuiBox>
              </Grid>
            </Grid>

            {/* Sección del Pagador de Libranza - Solo se muestra si naturaleza contiene "Libranza" */}
            {isLibranza && (
              <>
                <SuiBox>
                  <SuiTypography variant="h5" fontWeight="bold" color="info">
                    Información del Pagador de Libranza
                  </SuiTypography>
                </SuiBox>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.nombre_completo}
                        onChange={handlePagadorLibranzaChange("nombre_completo")}
                        placeholder="Nombre Completo del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        type="email"
                        value={formData.pagador_libranza.email}
                        onChange={handlePagadorLibranzaChange("email")}
                        placeholder="Email del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.celular}
                        onChange={handlePagadorLibranzaChange("celular")}
                        placeholder="Número de Celular del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                </Grid>
                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <Select
                        value={formData.pagador_libranza.tipo_identificacion}
                        onChange={handlePagadorLibranzaChange("tipo_identificacion")}
                        displayEmpty
                        fullWidth
                      >
                        <MenuItem value="">Seleccione Tipo Identificación</MenuItem>
                        <MenuItem value="CC">Cédula de Ciudadanía</MenuItem>
                        <MenuItem value="CE">Cédula de Extranjería</MenuItem>
                        <MenuItem value="NIT">NIT</MenuItem>
                        <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                      </Select>
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.identificacion}
                        onChange={handlePagadorLibranzaChange("identificacion")}
                        placeholder="Número de Identificación del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                  <Grid size={4}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.pagador_libranza.direccion}
                        onChange={handlePagadorLibranzaChange("direccion")}
                        placeholder="Dirección del Pagador"
                        fullWidth
                      />
                    </SuiBox>
                  </Grid>
                </Grid>
              </>
            )}

            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={4}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    ¿Conoce Intereses Causados?{"  "}
                    <Checkbox
                      checked={formData.reconoce_intereses}
                      onChange={handleChange("reconoce_intereses")}
                    />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              {formData.reconoce_intereses && (
                <Grid size={4}>
                  <SuiBox mb={2}>
                    <SuiBox mb={2}>
                      <SuiInput
                        value={formData.interesesFormatted}
                        onChange={handleMoneyChange("intereses")}
                        placeholder="Monto de los Intereses"
                        fullWidth
                      />
                    </SuiBox>
                  </SuiBox>
                </Grid>
              )}
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    ¿Es Codeudor o Avalista?{"  "}
                    <Checkbox
                      checked={formData.codeudor_avalista}
                      onChange={handleChange("codeudor_avalista")}
                    />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              {/* Agregar checkbox de postergado solo para clase 5 */}
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    ¿Acreedor Postergado?{"  "}
                    <Checkbox checked={formData.postergado} onChange={handleChange("postergado")} />
                  </SuiTypography>
                </SuiBox>
              </Grid>
              <Grid size={3}>
                <SuiBox mb={2}>
                  <SuiTypography variant="h6" fontWeight="dark">
                    ¿Costas?{"  "}
                    <Checkbox checked={formData.costas} onChange={handleChange("costas")} />
                  </SuiTypography>
                </SuiBox>
              </Grid>
            </Grid>
            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Fecha Otorgamiento
                </SuiTypography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    clearable
                    name="fecha_otorgamiento"
                    value={formData.fecha_otorgamiento ? dayjs(formData.fecha_otorgamiento) : null}
                    onChange={handleDateChange("fecha_otorgamiento")}
                    format="DD/MM/YYYY"
                    renderInput={(params) => (
                      <SuiInput
                        {...params}
                        icon={{
                          component: <CalendarTodayIcon />,
                          direction: "right",
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={6}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Fecha Vencimiento
                </SuiTypography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    clearable
                    name="fecha_vencimiento"
                    value={formData.fecha_vencimiento ? dayjs(formData.fecha_vencimiento) : null}
                    onChange={handleDateChange("fecha_vencimiento")}
                    format="DD/MM/YYYY"
                    renderInput={(params) => (
                      <SuiInput
                        {...params}
                        icon={{
                          component: <CalendarTodayIcon />,
                          direction: "right",
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </SuiBox>
        </DialogContent>
        <DialogActions>
          <SuiButton onClick={onClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton onClick={handleSubmit} variant="gradient" buttonColor="dark">
            Guardar
          </SuiButton>
        </DialogActions>
      </Dialog>

      <SelectAcreedorModal
        open={showAcreedorModal}
        onClose={() => setShowAcreedorModal(false)}
        onSelect={handleAcreedorSelect}
      />
    </>
  );
}

EditDeudaModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  deuda: PropTypes.object.isRequired,
  clasesDeuda: PropTypes.array.isRequired,
};

export default EditDeudaModal;
